import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerBrowserDownload } from '@crm/shared';
import { WhatsAppMediaAudio } from './WhatsAppMediaAudio';
import { WhatsAppVoiceNote } from './WhatsAppVoiceNote';
import { useWhatsAppMediaUrl } from './useWhatsAppMediaUrl';
import { whatsAppMessageApi } from './whatsAppMessageApi';
import { isVoiceNote } from './whatsAppMessageTypes';
import type { WhatsAppMessage } from './whatsAppMessageTypes';

/**
 * WHATSAPP MEDIA CONTENT — rende il media di un messaggio WhatsApp. Il binario è
 * servito da un endpoint autenticato (Bearer), quindi va scaricato come Blob:
 * - nota vocale: player + tasto di trascrizione ({@link WhatsAppVoiceNote}). Non è un
 *   tipo a sé — è un AUDIO con un flag, perché così lo modella Meta;
 * - immagine/video/audio: reso inline, serve un object-URL come `src`
 *   ({@link useWhatsAppMediaUrl});
 * - documento: non ha una resa inline, quindi non si pre-scarica; al click si scarica
 *   il Blob e lo si salva col nome originale riusando il helper condiviso
 *   {@link triggerBrowserDownload} (stesso "save file" degli allegati email).
 */
interface WhatsAppMediaContentProps {
  message: WhatsAppMessage;
}

export function WhatsAppMediaContent({ message }: WhatsAppMediaContentProps) {
  if (isVoiceNote(message)) {
    return <WhatsAppVoiceNote message={message} />;
  }
  if (message.type === 'DOCUMENT') {
    return <WhatsAppMediaDocument message={message} />;
  }
  return <WhatsAppMediaInline message={message} />;
}

/**
 * Immagine/video/audio allegato: resa inline dall'object-URL del binario.
 */
function WhatsAppMediaInline({ message }: WhatsAppMediaContentProps) {
  // L'audio ha un componente suo, condiviso con la nota vocale: qui resterebbe un ramo
  // identico da tenere allineato a mano.
  if (message.type === 'AUDIO') {
    return <WhatsAppMediaAudio message={message} />;
  }

  return <WhatsAppMediaVisual message={message} />;
}

/**
 * Immagine e video: object-URL nel `src`, coi tre stati di caricamento.
 */
function WhatsAppMediaVisual({ message }: WhatsAppMediaContentProps) {
  const { t } = useTranslation();
  const { url, failed } = useWhatsAppMediaUrl(message.whatsAppMessageId);

  if (failed) {
    return (
      <span className="wa-media wa-media--error">
        {t('whatsapp.media.error')}
      </span>
    );
  }
  if (!url) {
    return (
      <span className="wa-media wa-media--loading">
        {t('whatsapp.media.loading')}
      </span>
    );
  }

  const fileName = message.mediaFileName ?? '';

  switch (message.type) {
    case 'IMAGE':
      return (
        <img className="wa-media wa-media--image" src={url} alt={fileName} />
      );
    case 'VIDEO':
      return <video className="wa-media wa-media--video" src={url} controls />;
    default:
      return null;
  }
}

/**
 * Documento: nessuna resa inline, il binario si scarica solo al click e viene salvato
 * su disco col nome originale tramite il helper condiviso.
 */
function WhatsAppMediaDocument({ message }: WhatsAppMediaContentProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const fileName = message.mediaFileName || t('whatsapp.media.document');

  const handleDownload = () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    whatsAppMessageApi
      .getMediaContent(message.whatsAppMessageId)
      .then((blob) => triggerBrowserDownload(blob, fileName))
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  return (
    <button
      type="button"
      className="wa-media wa-media--doc"
      onClick={handleDownload}
      disabled={busy}
    >
      {failed ? t('whatsapp.media.error') : fileName}
    </button>
  );
}

export default WhatsAppMediaContent;
